// Constants
import { EVENT_DELAY } from '@src/config/constants'
import { getCheckoutToken } from '@src/quince-site-common/src/hooks/cart/hooks/new-cart-helper'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { DEFAULT_CURRENCY_CODE } from '@src/quince-site-common/src/utils/constants'
import { TELEMETRY_DOMAINS } from '@src/telemetry/telemetry-domains.constants'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { TelemetryIntegration } from '@src/telemetry/telemetry-events.types'
import { getStorefrontIdProperty } from '@src/utils/b2b'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'

import { processImageUrl } from '../provider-utils/data-layer-pixel-utils'

export const Attentive: TelemetryIntegration = {
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
    domain: TELEMETRY_DOMAINS.ATTENTIVE,
    featureGateName: 'ssr_telemetry_attentive',
  },
  events: {
    [TELEMETRY_EVENT_NAMES.PRODUCT_VIEW]: (properties: Record<string, unknown>) => {
      const productImageUrl = new URL(processImageUrl(properties.variantImageUrl as string))
      // eslint-disable-next-line i18next/no-literal-string
      productImageUrl.searchParams.set('w', '300')
      const localisationData = getLocalisationDataFromStorage()

      const localeProperty = localisationData
        ? {
            locale: {
              language: localisationData?.language,
              country: localisationData?.country,
            },
          }
        : {}
      trackAttentiveConversion('productView', {
        items: [
          {
            productId: properties.productId as string,
            productVariantId: properties.variantId as string,
            price: {
              value: properties.price as number,
              currency: DEFAULT_CURRENCY_CODE,
            },
            name: properties.productTitle as string,
            productImage: productImageUrl.toString(),
          },
        ],
        ...localeProperty,
        user: {
          email: AuthUtil.isLoggedIn() ? (AuthUtil.getUser().email ?? undefined) : undefined,
        },
      })
    },

    [TELEMETRY_EVENT_NAMES.ADD_TO_CART]: (properties: Record<string, unknown>) => {
      const productImageUrl = new URL(processImageUrl(properties.variantImageUrl as string))
      // eslint-disable-next-line i18next/no-literal-string
      productImageUrl.searchParams.set('w', '300')
      const localisationData = getLocalisationDataFromStorage()
      const localeProperty = localisationData
        ? {
            locale: {
              language: localisationData?.language,
              country: localisationData?.country,
            },
          }
        : {}

      const value = properties?.value ?? properties.price

      trackAttentiveConversion('addToCart', {
        items: [
          {
            productId: properties.productId as string,
            productVariantId: properties.variantId as string,
            price: {
              value: Number(value),
              currency: DEFAULT_CURRENCY_CODE,
            },
            name: properties.productTitle as string,
            productImage: productImageUrl.toString(),
          },
        ],
        ...localeProperty,
        cart: {
          cartId: getCheckoutToken(),
        },
        user: {
          email: AuthUtil.isLoggedIn() ? (AuthUtil.getUser().email ?? undefined) : undefined,
        },
      })
    },
  },
}

interface AttentiveEventDataPrice {
  value: number
  currency: string
}

interface AttentiveEventDataItem {
  productId: string
  productVariantId: string
  price: AttentiveEventDataPrice
  name?: string
  productImage?: string
  category?: string
  quantity?: number
}

interface AttentiveEventDataCart {
  cartId?: string
  cartCoupon?: string
}

interface AttentiveEventDataUser {
  phone?: string
  email?: string
}

interface AttentiveEventDataLocale {
  language: string
  country: string
}

interface ProductViewEventProperties {
  items: AttentiveEventDataItem
  user?: AttentiveEventDataUser
  locale?: AttentiveEventDataLocale
}

interface AddToCartEventProperties {
  items: AttentiveEventDataItem[]
  cart?: AttentiveEventDataCart
  user?: AttentiveEventDataUser
  locale?: AttentiveEventDataLocale
}

type AttentiveConversionEventProperties = ProductViewEventProperties | AddToCartEventProperties

const trackAttentiveConversion = (
  actionId: 'productView' | 'addToCart',
  properties: AttentiveConversionEventProperties,
) => {
  executeFireAndForgotTask(() => {
    if (window.attentive?.analytics?.[actionId]) {
      window.attentive.analytics[actionId]({
        ...properties,
        ...getStorefrontIdProperty(), //storefront_id is used to identify the b2b self serve flow
      })
    }
  }, EVENT_DELAY)
}
